import React from "react";
import {
  FolderIcon,
  VideoCameraIcon,
  FolderPlusIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import { Folder } from "../types";

interface CompactVideoHeaderProps {
  currentFolder: Folder | null;
  totalFolderCount: number;
  totalVideoCount: number;
  totalFileCount: number;
  openCreateFolderModal: () => void;
  openUploadModal: () => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  handleSearch: (e: React.FormEvent) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  fileTypeFilter?: string;
  setFileTypeFilter?: (type: string) => void;
  resetFilters: () => void;
  videoCount: number;
  selectedCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onMoveSelected: () => void;
}

const CompactVideoHeader: React.FC<CompactVideoHeaderProps> = ({
  currentFolder,
  totalFolderCount,
  totalVideoCount,
  totalFileCount,
  openCreateFolderModal,
  openUploadModal,
  searchTerm,
  setSearchTerm,
  handleSearch,
  statusFilter,
  setStatusFilter,
  fileTypeFilter,
  setFileTypeFilter,
  resetFilters,
  videoCount,
  selectedCount,
  onSelectAll,
  onClearSelection,
  onMoveSelected,
}) => {
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-2 mb-4">
      <div className="flex flex-col lg:flex-row items-center gap-3">
        {/* Left Section: Folder Info */}
        <div className="flex items-center space-x-3 min-w-max">
          <div className="bg-indigo-50 p-1.5 rounded-lg shrink-0">
            {currentFolder ? (
              <FolderIcon className="h-5 w-5 text-indigo-600" />
            ) : (
              <VideoCameraIcon className="h-5 w-5 text-indigo-600" />
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-sm font-bold text-gray-900 truncate max-w-[120px]">
              {currentFolder ? currentFolder.name : "ວິດີໂອ"}
            </h1>
            <div className="flex items-center space-x-1.5 mt-0.5">
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium whitespace-nowrap">
                {currentFolder
                  ? currentFolder.video_count || 0
                  : totalVideoCount}{" "}
                ວິດີໂອ
              </span>
              {((currentFolder ? currentFolder.file_count : totalFileCount) ||
                0) > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600 font-medium whitespace-nowrap">
                  {currentFolder ? currentFolder.file_count : totalFileCount}{" "}
                  ໄຟລ໌
                </span>
              )}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 font-medium whitespace-nowrap">
                {currentFolder
                  ? currentFolder.subfolder_count || 0
                  : totalFolderCount}{" "}
                ໂຟລເດີ
              </span>
            </div>
          </div>
        </div>

        <div className="h-6 w-px bg-gray-200 hidden lg:block mx-1"></div>

        {/* Middle Section: Search & Filters */}
        <div className="flex flex-1 items-center gap-2 w-full">
          <form onSubmit={handleSearch} className="relative flex-1 max-w-xs">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded-lg bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs transition-all"
              placeholder="ຄົ້ນຫາ..."
            />
          </form>

          {/* Type Filter */}
          <div className="relative shrink-0">
            <select
              value={fileTypeFilter || ""}
              onChange={(e) => setFileTypeFilter?.(e.target.value)}
              className="block pl-2 pr-8 py-1.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 text-[11px] appearance-none"
            >
              <option value="">ທຸກປະເພດ</option>
              <option value="video">ວິດີໂອ</option>
              <option value="pdf">PDF</option>
              <option value="document">ເອກະສານ</option>
              <option value="image">ຮູບພາບ</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <FunnelIcon className="h-3 w-3 text-gray-400" />
            </div>
          </div>

          {/* Status Filter */}
          <div className="relative shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block pl-2 pr-8 py-1.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 text-[11px] appearance-none"
            >
              <option value="">ທັງໝົດ</option>
              <option value="ready">ພ້ອມແລ້ວ</option>
              <option value="published">ເຜີຍແຜ່</option>
              <option value="archived">ເກັບໄວ້</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <FunnelIcon className="h-3 w-3 text-gray-400" />
            </div>
          </div>

          <button
            onClick={resetFilters}
            className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50"
            title="Reset"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="h-6 w-px bg-gray-200 hidden lg:block mx-1"></div>

        {/* Right Section: Add & Selection */}
        <div className="flex items-center gap-2">
          {/* Add Buttons */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={openCreateFolderModal}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <FolderPlusIcon className="h-3.5 w-3.5 mr-1.5" />
              <span className="hidden xl:inline">ເພີ່ມໂຟລເດີ</span>
            </button>
            <button
              onClick={openUploadModal}
              className="inline-flex items-center px-2.5 py-1.5 border border-transparent rounded-lg text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="h-3.5 w-3.5 mr-1.5" />
              <span className="hidden xl:inline">ເພີ່ມໄຟລ໌ໃໝ່</span>
              <span className="xl:hidden">ເພີ່ມໃໝ່</span>
            </button>
          </div>

          <div className="h-6 w-px bg-gray-200 mx-1"></div>

          {/* Selection Logic */}
          {selectedCount > 0 ? (
            <div className="flex items-center gap-1.5 bg-blue-50 px-2 py-1 rounded-lg border border-blue-100">
              <span className="text-[10px] font-bold text-blue-700 mx-1">
                {selectedCount}
              </span>
              <button
                onClick={onMoveSelected}
                className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                title="Move"
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                <span className="sr-only">Move</span>
              </button>
              <button
                onClick={onClearSelection}
                className="p-1 text-gray-500 hover:bg-blue-100 rounded transition-colors"
                title="Clear"
              >
                <XMarkIcon className="h-4 w-4" />
                <span className="sr-only">Clear</span>
              </button>
            </div>
          ) : (
            <button
              onClick={onSelectAll}
              disabled={videoCount === 0}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 rounded-lg text-[10px] font-bold text-gray-600 bg-gray-50 hover:bg-white disabled:opacity-50"
            >
              <CheckIcon className="h-3.5 w-3.5 mr-1" />
              ເລືອກທັງໝົດ
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompactVideoHeader;
